import type {
  AdmissionSettingsDto,
  AdmissionSettingsInput,
  DuplicateCheckDto,
  PersonInput,
  SectionDto,
  StudentCreateInput,
  StudentDto,
  StudentHistoryEntry,
  StudentProfileDto,
  StudentUpdate,
  TablePage,
} from "@eduleo/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSessionKey } from "../../app/session-sync";
import type { TableParams } from "../../hooks/use-table-params";
import { ApiError, apiDownload, apiDownloadFile, apiRequest } from "../../lib/api-client";
import { saveFile } from "../../lib/save-file";

const seg = encodeURIComponent;

/** Keys include who is signed in, so one person's cached list is never shown to another. */
export const studentKeys = {
  all: ["students"] as const,
  list: (session: string | null, query: Record<string, unknown>) =>
    ["students", session, "list", query] as const,
  one: (session: string | null, id: string) => ["students", session, "one", id] as const,
  history: (session: string | null, id: string) => ["students", session, "history", id] as const,
  photo: (session: string | null, path: string, version: string) =>
    ["students", session, "photo", path, version] as const,
  settings: (session: string | null) => ["admission-settings", session] as const,
  nextNumber: (session: string | null) => ["admission-settings", session, "next"] as const,
};

/** The table's filters as the API takes them. */
function filtersOf(params: TableParams) {
  return {
    q: params.q,
    sectionId: params.filters.sectionId,
    status: params.filters.status,
    portal: params.filters.portal,
    sort: params.sort,
  };
}

/** One page of the students table, as the table's address describes it. */
export function useStudentsTable(params: TableParams) {
  const session = useSessionKey();
  const query = { ...filtersOf(params), limit: params.pageSize, cursor: params.cursor };
  return useQuery({
    queryKey: studentKeys.list(session, query),
    queryFn: ({ signal }) => apiRequest<TablePage<StudentDto>>("/students", { signal, query }),
    // Keep the rows on screen (dimmed) while the next page or order loads.
    placeholderData: keepPreviousData,
    enabled: Boolean(session),
  });
}

/** Everything about one student the person may see, and what's still missing. */
export function useStudent(id: string) {
  const session = useSessionKey();
  return useQuery({
    queryKey: studentKeys.one(session, id),
    queryFn: ({ signal }) => apiRequest<StudentProfileDto>(`/students/${seg(id)}`, { signal }),
    enabled: Boolean(session) && Boolean(id),
  });
}

export function useStudentHistory(id: string, enabled: boolean) {
  const session = useSessionKey();
  return useQuery({
    queryKey: studentKeys.history(session, id),
    queryFn: async ({ signal }) =>
      (
        await apiRequest<{ items: StudentHistoryEntry[] }>(`/students/${seg(id)}/history`, {
          signal,
        })
      ).items,
    enabled: Boolean(session) && Boolean(id) && enabled,
  });
}

/**
 * A photo (the student's, or one of their people's) as a page-local address, cached per
 * version so a new photo is fetched and an old one never shown. Null without a photo.
 */
export function usePhotoUrl(path: string, version: string | null) {
  const session = useSessionKey();
  const query = useQuery({
    queryKey: studentKeys.photo(session, path, version ?? ""),
    queryFn: async ({ signal }) => URL.createObjectURL(await apiDownload(path, { signal })),
    enabled: Boolean(session) && version !== null,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * 60_000,
  });
  return version === null ? null : (query.data ?? null);
}

export function useAdmissionSettings() {
  const session = useSessionKey();
  return useQuery({
    queryKey: studentKeys.settings(session),
    queryFn: ({ signal }) => apiRequest<AdmissionSettingsDto>("/admission-settings", { signal }),
    enabled: Boolean(session),
    staleTime: 60_000,
  });
}

export function useNextAdmissionNumber(enabled: boolean) {
  const session = useSessionKey();
  return useQuery({
    queryKey: studentKeys.nextNumber(session),
    queryFn: ({ signal }) =>
      apiRequest<{ admissionNo: string }>("/admission-settings/next-number", { signal }),
    enabled: Boolean(session) && enabled,
  });
}

export function useSaveAdmissionSettings() {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  return useMutation({
    mutationFn: (body: { settings: AdmissionSettingsInput; version: number }) =>
      apiRequest<AdmissionSettingsDto>("/admission-settings", { method: "PUT", body }),
    onSuccess: (saved) => {
      queryClient.setQueryData(studentKeys.settings(session), saved);
      void queryClient.invalidateQueries({ queryKey: studentKeys.nextNumber(session) });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: studentKeys.settings(session) });
      }
    },
  });
}

export function useCheckDuplicates() {
  return useMutation({
    mutationFn: (body: {
      firstName: string;
      lastName: string;
      dateOfBirth?: string;
      mobile?: string;
    }) => apiRequest<DuplicateCheckDto>("/students/duplicates", { method: "POST", body }),
  });
}

/** Sections for filters and forms. Families can't list sections, so a 403 means "no filter". */
export function useSections() {
  const session = useSessionKey();
  return useQuery({
    queryKey: ["sections", session],
    queryFn: async ({ signal }) => {
      try {
        return (await apiRequest<{ items: SectionDto[] }>("/sections", { signal })).items;
      } catch (error) {
        if (error instanceof ApiError && error.code === "FORBIDDEN") return [];
        throw error;
      }
    },
    staleTime: 10 * 60_000,
  });
}

export function useCreateStudent() {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  return useMutation({
    mutationFn: (input: StudentCreateInput) =>
      apiRequest<StudentProfileDto>("/students", { method: "POST", body: input }),
    onSuccess: (saved) => {
      // The new student's page opens with what the save returned, without loading it again.
      queryClient.setQueryData(studentKeys.one(session, saved.id), saved);
      void queryClient.invalidateQueries({
        queryKey: studentKeys.all,
        predicate: (query) => query.queryKey[3] !== saved.id,
      });
    },
  });
}

export function useUpdateStudent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: StudentUpdate }) =>
      apiRequest<StudentDto>(`/students/${seg(id)}`, { method: "PATCH", body }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: studentKeys.all }),
  });
}

export function useRemoveStudent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/students/${seg(id)}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: studentKeys.all }),
  });
}

/** Downloads the students the table lists (filters and order), as CSV. */
export function useDownloadStudents() {
  return useMutation({
    mutationFn: async (params: TableParams) => {
      const { blob, fileName } = await apiDownloadFile("/students/export", {
        query: filtersOf(params),
      });
      saveFile(blob, fileName ?? "students.csv");
    },
  });
}

/** Changes to one student's people, documents and photos; each answer reloads the student. */
export type StudentChange =
  | { kind: "add-person"; person: PersonInput }
  | {
      kind: "update-person";
      personId: string;
      person: PersonInput;
      version: number;
      personVersion: number;
    }
  | { kind: "remove-person"; personId: string; version: number }
  | { kind: "person-photo"; personId: string; file: File }
  | { kind: "photo"; file: File }
  | { kind: "upload-document"; documentKind: string; file: File }
  | {
      kind: "review-document";
      documentId: string;
      decision: "verified" | "rejected";
      note: string | null;
      version: number;
    }
  | { kind: "remove-document"; documentId: string; version: number };

function fileForm(file: File, kind?: string): FormData {
  const form = new FormData();
  form.set("file", file, file.name);
  if (kind) form.set("kind", kind);
  return form;
}

function sendChange(id: string, change: StudentChange): Promise<unknown> {
  const base = `/students/${seg(id)}`;
  switch (change.kind) {
    case "add-person":
      return apiRequest(`${base}/people`, { method: "POST", body: change.person });
    case "update-person":
      return apiRequest(`${base}/people/${seg(change.personId)}`, {
        method: "PATCH",
        body: {
          person: change.person,
          version: change.version,
          personVersion: change.personVersion,
        },
      });
    case "remove-person":
      return apiRequest(`${base}/people/${seg(change.personId)}`, {
        method: "DELETE",
        query: { version: change.version },
      });
    case "person-photo":
      return apiRequest(`${base}/people/${seg(change.personId)}/photo`, {
        method: "PUT",
        body: fileForm(change.file),
      });
    case "photo":
      return apiRequest(`${base}/photo`, { method: "PUT", body: fileForm(change.file) });
    case "upload-document":
      return apiRequest(`${base}/documents`, {
        method: "POST",
        body: fileForm(change.file, change.documentKind),
      });
    case "review-document":
      return apiRequest(`${base}/documents/${seg(change.documentId)}/review`, {
        method: "POST",
        body: { decision: change.decision, note: change.note, version: change.version },
      });
    case "remove-document":
      return apiRequest(`${base}/documents/${seg(change.documentId)}`, {
        method: "DELETE",
        query: { version: change.version },
      });
  }
}

export function useChangeStudent(id: string) {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  return useMutation({
    mutationFn: (change: StudentChange) => sendChange(id, change),
    // Stays pending until the student is reloaded, so "saved" never shows next to old data.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: studentKeys.one(session, id) }),
        queryClient.invalidateQueries({ queryKey: studentKeys.history(session, id) }),
      ]),
  });
}

/** Saves one of the student's documents to the device (never opened inside the app). */
export async function downloadStudentDocument(studentId: string, documentId: string) {
  const { blob, fileName } = await apiDownloadFile(
    `/students/${seg(studentId)}/documents/${seg(documentId)}/file`,
  );
  saveFile(blob, fileName ?? "document");
}
